import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'progetti',
  titolo: 'Progetti',
  gruppo: 'Rete e progetti',
  permesso: 'progetto.leggi',
  aCheServe: 'La voce «Progetti» è l’anagrafica dei progetti: codici, periodo, enti, sedi, work package, scadenze '
    + 'di rendicontazione e loghi da mettere nelle stampe.',
  parti: [
    {
      titolo: 'Trovare un progetto',
      passi: [
        'Apri «Progetti». Cerca per titolo, codice, CUP o CIG.',
        'Restringi con «Stato» (da avviare, in corso, concluso), «Fondo», «Area principale», «Sede CIDIS» e «Comune».',
        'Premi il titolo per aprire la scheda del progetto.',
      ],
      immagine: {
        file: 'progetti.webp', larghezza: 1200, altezza: 800,
        descrizione: 'L’elenco dei progetti con periodo, stato, capofila e sedi.',
      },
    },
    {
      titolo: 'Aggiornare l’andamento',
      testo: [
        'Chi rendiconta, operatori interni compresi, aggiorna il lavoro fatto. Nel riquadro «Work package e '
          + 'attività», «Andamento» aggiorna stato, avanzamento e ore svolte di un’attività.',
        'Nel riquadro «Scadenze di rendicontazione» si aggiorna lo «Stato» di una scadenza: qualche giorno prima '
          + 'arriva il promemoria al referente e al responsabile.',
      ],
    },
    {
      titolo: 'Creare e modificare un progetto',
      permesso: 'progetto.gestisci',
      passi: [
        'Premi «Nuovo progetto»: compila identificazione, fondo, area, referente CIDIS e periodo, poi salva.',
        'Nella scheda del progetto ogni riquadro ha il suo «Modifica»: enti, territorio e sedi, destinatari, '
          + 'work package, scadenze.',
        'Quando scegli il referente CIDIS, il gestionale chiede se concedergli l’accesso ai dati del progetto: '
          + '«Sì, concedi l’accesso» con il motivo, oppure «No, solo dato anagrafico».',
        'Se il progetto ha una sua casella di posta, spunta «Il progetto ha una casella di posta dedicata»: vedi '
          + '«La casella di posta del progetto».',
      ],
    },
    {
      titolo: 'La casella di posta del progetto',
      permesso: 'progetto.gestisci',
      testo: [
        'Una casella del progetto, senza inoltri verso le caselle personali: si apre dalla pagina «Posta» o dal '
          + 'riquadro «Posta del progetto» con «Apri in Posta». La aprono il **referente CIDIS** del progetto e chi '
          + 'ha l’incarico di **Project Manager su questo progetto**, sempre con «legge e scrive». Chi è Project '
          + 'Manager su tutta CIDIS non la vede, se non lo aggiungi.',
      ],
      passi: [
        'Nel progetto nuovo spunta «Il progetto ha una casella di posta dedicata»; in un progetto che c’è già, '
          + 'riquadro «Posta del progetto», premi «Crea la casella».',
        'Scegli il prefisso, «progetto-» oppure «pg-», e scrivi la parte dopo il trattino: lettere minuscole, cifre, '
          + 'punto e trattino. Sotto il campo leggi l’indirizzo che avrà, per esempio progetto-supreme2.calabria@'
          + 'cidisonlus.org.',
        'In «Chi altro accede» aggiungi le persone con il livello: «legge» apre le conversazioni e basta, «legge e '
          + 'scrive» risponde, invia e archivia.',
        'Con «Modifica» del riquadro cambi il nome che vede chi riceve, la disattivi o la riattivi. L’indirizzo si '
          + 'cambia solo finché la casella non ha messaggi.',
      ],
      nota: 'Prima che la posta di CIDIS passi al gestionale, la casella non riceve: il riquadro lo dice. Le caselle '
        + 'di progetto che esistono già sul vecchio server tengono il loro indirizzo e le collega chi gestisce gli '
        + 'utenti.',
    },
    {
      titolo: 'Chi accede alla casella del progetto',
      testo: [
        'Nel riquadro «Posta del progetto» il referente CIDIS e i Project Manager del progetto vedono «Chi accede»: '
          + 'aggiungono una persona con il livello e «Dai l’accesso», cambiano il livello o premono «Togli». Lo può '
          + 'fare anche chi gestisce gli utenti. Ogni passo resta nel registro.',
        'Chi perde l’incarico sul progetto, o non è più referente, non apre più la casella dalla pagina successiva.',
      ],
    },
    {
      titolo: 'I loghi nelle stampe',
      permesso: 'progetto.gestisci',
      passi: [
        'Nella scheda del progetto, riquadro «Loghi nelle stampe», premi «Aggiungi un logo».',
        'Scrivi il nome (per esempio «Ministero dell’Interno – FAMI»), scegli il file PNG o JPEG (al massimo 2 MB) '
          + 'e premi «Carica il logo».',
        'Con «Su» e «Giù» cambi l’ordine; «Togli» lo leva dalle stampe del progetto.',
      ],
      nota: 'Gli SVG non sono accettati: converti prima il logo in PNG.',
    },
    {
      titolo: 'Il modulo del destinatario',
      permesso: 'progetto.gestisci',
      passi: [
        'Nella pagina del progetto, riquadro «Modulo del destinatario», premi «Parti dal modello FAMI 2021-2027» '
          + 'e scegli la versione essenziale o completa.',
        'Correggi codice identificativo, beneficiario finale, titolo, titolare del trattamento e allegati; '
          + 'scegli i loghi fra quelli del progetto.',
        'Riordina o togli i campi con «Su», «Giù» e «Togli», poi «Salva il modulo».',
      ],
      nota: 'Il titolare del trattamento è quello del progetto, che può non essere CIDIS.',
    },
  ],
  avvertenze: [
    {
      quando: 'Accanto al referente leggi che la richiesta di accesso è ancora aperta.',
      cosaFare: 'Non puoi assegnare incarichi su quel progetto: decide chi può, nella pagina «Approvazioni».',
    },
    {
      quando: '«Questo indirizzo è già di un’altra casella» quando crei la casella del progetto.',
      cosaFare: 'Scegli un’altra parte dopo il trattino, oppure l’altro prefisso: due caselle non hanno lo stesso '
        + 'indirizzo, di nessun tipo.',
    },
    {
      quando: 'Nel riquadro «Posta del progetto» non vedi «Chi accede» né «Apri in Posta».',
      cosaFare: 'Non accedi alla casella: chiedi al referente CIDIS o al Project Manager del progetto di aggiungerti.',
    },
  ],
  vediAnche: ['enti', 'scheda', 'posta'],
  parole: ['progetto', 'fondo', 'fami', 'wp', 'work package', 'rendicontazione', 'loghi', 'cup', 'cig',
    'casella del progetto', 'pg-', 'email del progetto'],
}

export default capitolo
